import {
    format,
    formatDistanceToNow,
    isToday,
    parseISO,
    differenceInMinutes,
    formatDuration,
} from 'date-fns';
import { es, pl } from 'date-fns/locale';
import { COLOURS, LANGUAGES } from './consts';

const emailCheck =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const capitalizeFirstLetter = (string: string): string =>
    string && string[0].toUpperCase() + string.slice(1);

export const isValidEmail = (email: string) =>
    emailCheck.test(email.toLocaleLowerCase());

export const isValidPassword = (pw: string) => {
    return (
        /[A-Z]/.test(pw) &&
        /[a-z]/.test(pw) &&
        /[0-9]/.test(pw) &&
        /[^-!@._*#%+]/.test(pw) &&
        /[^A-Za-z0-9]/.test(pw) &&
        pw.length > 4
    );
};

export const renderVoteEndsDate = (date: string) =>
    format(new Date(date), 'dd/MM/yyyy, HH:mm');

export function renderUserName(
    name: string | undefined,
    surname: string | undefined
) {
    if (!name || !surname) {
        return 'Invalid name or surname';
    }
    return `${name} ${surname.charAt(0)}.`;
}

export function scrollIntoViewIfNeeded(target: HTMLElement | null) {
    if (!target) {
        return;
    }
    // Target is outside the viewport from the bottom
    if (target.getBoundingClientRect().bottom > window.innerHeight) {
        //  The bottom of the target will be aligned to the bottom of the visible area of the scrollable ancestor.
        target.scrollIntoView(false);
    }

    // Target is outside the view from the top
    if (target.getBoundingClientRect().top < 0) {
        // The top of the target will be aligned to the top of the visible area of the scrollable ancestor
        target.scrollIntoView({
            behavior: 'auto',
            block: 'end',
            inline: 'nearest',
        });
    }
}

export function scrollAndFocusFirstErrorField<TField extends string>(
    firstErrorField: TField | null,
    fieldSelectorByName: Partial<Record<TField, string>>
) {
    if (!firstErrorField) {
        return;
    }

    const selector = fieldSelectorByName[firstErrorField];
    if (!selector) {
        return;
    }

    const firstInputToFocus = document.querySelector(
        selector
    ) as HTMLElement | null;

    requestAnimationFrame(() => {
        firstInputToFocus?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
        });
        firstInputToFocus?.focus();
    });
}

export function formatDistanceWithLocale(date: string, language: LANGUAGES) {
    switch (language) {
        case LANGUAGES.EN:
            return formatDistanceToNow(new Date(date));
        case LANGUAGES.PL:
            return formatDistanceToNow(new Date(date), { locale: pl });
        case LANGUAGES.ES:
            return formatDistanceToNow(new Date(date), { locale: es });
        default:
            return formatDistanceToNow(new Date(date));
    }
}

type EventDateLabels = {
    todayLabel: string;
    atLabel?: string;
    showTime?: boolean;
};

export function renderEventDate(
    date: Date | string,
    language: LANGUAGES | undefined,
    labels: EventDateLabels
) {
    const parsedDate = new Date(date);
    const locale = language === LANGUAGES.PL ? pl : undefined;
    const showTime = labels.showTime!!;

    if (isToday(parsedDate)) {
        return showTime
            ? `${labels.todayLabel} ${labels.atLabel} ${format(
                  parsedDate,
                  'HH:mm',
                  { locale }
              )}`
            : labels.todayLabel;
    }

    return showTime
        ? `${format(parsedDate, 'MMM d', { locale })} ${
              labels.atLabel
          } ${format(parsedDate, 'HH:mm', { locale })}`
        : format(parsedDate, 'MMMM d', { locale });
}

export function renderEventTime(
    date: Date | string,
    language: LANGUAGES | undefined
) {
    const parsedDate = new Date(date);
    const locale = language === LANGUAGES.PL ? pl : undefined;

    return `${format(parsedDate, 'HH:mm', { locale })}`;
}

export function upsert<T>(arr: T[], item: T, key: keyof T): T[] {
    const index = arr.findIndex(x => x[key] === item[key]);
    if (index === -1) {
        return [...arr, item]; // push
    }
    const copy = [...arr];
    copy[index] = item;
    // replace
    return copy;
}

export function truncateToOneDecimal(num: number) {
    return Math.trunc(num * 10) / 10;
}

export function getRatingColor(score?: number) {
    if (!score) {
        return COLOURS.gray;
    }
    if (score >= 1 && score < 2) return '#f44431'; // light red
    if (score >= 2 && score < 3) return '#ef830e'; // orange
    if (score >= 3 && score < 4) return '#31c66e'; // light green
    if (score >= 4 && score < 4.5) return '#329F5B'; // green
    if (score >= 4.5 && score <= 5) return '#374DF5'; // blue

    return '#CCCCCC'; // default
}

export function setPageTitle(title: string) {
    if (typeof document === 'undefined') {
        return;
    }

    document.title = title;
}

export { resolveSiteLocale as getSiteLocaleFromPathname, localizePath as withSiteLocale } from './siteLocale';

export function formatVoteDateDistance(
    endsAtISO: string,
    language?: LANGUAGES
) {
    const endsAt = parseISO(endsAtISO);
    const now = new Date();
    const locale = language === LANGUAGES.PL ? pl : undefined;

    const totalMinutes = Math.max(0, differenceInMinutes(endsAt, now));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${formatDuration(
        { hours, minutes },
        { format: ['hours', 'minutes'], locale, zero: false }
    )}`;
}
